import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import LayoutManager from "./layout-manager";
import { DragDropContext } from "react-dnd";
import DragBackend from "lib/dnd-backend";

import { PageMonitor, ElementMonitor } from "./monitors";

const styles = {
    runway: {
        position: "relative",
        width: "100%",
        // contain: "layout", // breaks in chrome 69
    },
    runwayFixedHeight: (height) => {
        return height
            ? {
                  overflowX: "none",
                  overflowY: "auto",
                  height,
              }
            : {};
    },
    sentinel: {
        position: "absolute",
        height: "1px",
        width: "1px",
        transition: "transform 0.2s",
    },
};

class InfiniteScrollContainer extends PureComponent {
    static propTypes = {
        layoutManager: PropTypes.instanceOf(LayoutManager).isRequired,
        onLoadMoreRows: PropTypes.func.isRequired,
        loading: PropTypes.bool.isRequired,
        loadingSpinner: PropTypes.node,
        hasMoreElements: PropTypes.bool.isRequired,
        emptyResults: PropTypes.node.isRequired,
        dataTest: PropTypes.string,
        onMove: PropTypes.func,
        onDrop: PropTypes.func,
        dragDrop: PropTypes.bool,
        loadingHeight: PropTypes.number,
        offset: PropTypes.number,
        // makes the scroll within the component instead of the page
        height: PropTypes.number,
        className: PropTypes.string,
        sidePanelWidth: PropTypes.number, // prop for assortments panel
    };

    static defaultProps = {
        loadingSpinner: <div>Loading</div>,
        loadingHeight: 80,
        offset: 0,
        dragDrop: false,
        dataTest: "infinite-scroll",
    };

    constructor(props) {
        super(props);
        const Monitor = props.height ? ElementMonitor : PageMonitor;

        this.state = {
            visibleComponents: [],
            style: Object.assign({}, styles.runway, styles.runwayFixedHeight(props.height)),
        };

        this.monitor = new Monitor(props.layoutManager, this.handleRepaint.bind(this));
        this.handleRepaint = this.handleRepaint.bind(this);
    }

    UNSAFE_componentWillMount() {
        const { dragDrop, layoutManager } = this.props;
        layoutManager.on("refresh", this.refreshComponent, this);
        layoutManager.on("remove", this.removeComponent, this);
        if (dragDrop) {
            layoutManager.on("drag", this.dragComponent, this);
            layoutManager.on("move", this.moveComponent, this);
            layoutManager.on("drop", this.dropComponent, this);
            layoutManager.enableDragDrop();
        }
    }

    componentDidMount() {
        this.monitor.watch(this._scrollingElement);
    }

    componentWillUnmount() {
        const { dragDrop, layoutManager } = this.props;
        this.monitor.unwatch();

        layoutManager.off("refresh", this.refreshComponent, this);
        layoutManager.off("remove", this.removeComponent, this);

        if (dragDrop) {
            layoutManager.off("drag", this.dragComponent, this);
            layoutManager.off("drop", this.dropComponent, this);
            layoutManager.off("move", this.moveComponent, this);
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (this.props.dragAndDrop !== nextProps.dragAndDrop) {
            const { layoutManager } = nextProps;
            if (nextProps.dragAndDrop) {
                layoutManager.enableDragAndDrop();
            } else {
                layoutManager.disableDragAndDrop();
            }
        }

        if (
            (nextProps.loading !== this.props.loading && nextProps.loading === false) ||
            nextProps.hasMoreElements !== this.props.hasMoreElements
        ) {
            this.setState({
                visibleComponents: nextProps.layoutManager.getVisibleComponents(
                    this.monitor.scrollTop,
                    this.monitor.scrollLeft,
                ),
            });
        }
    }

    componentDidUpdate(prevProps, prevState) {
        // when resizing the assortments side panel
        if (this.props.sidePanelWidth !== prevProps.sidePanelWidth) {
            this.monitor.handleResize();
        }
    }

    removeComponent() {
        const { layoutManager } = this.props;
        this.setState({
            visibleComponents: layoutManager.getVisibleComponents(
                this.monitor.scrollTop,
                this.monitor.scrollLeft,
                true,
            ),
        });
    }

    refreshComponent() {
        const { layoutManager } = this.props;
        this.setState({
            visibleComponents: layoutManager.getVisibleComponents(
                this.monitor.scrollTop,
                this.monitor.scrollLeft,
                true,
            ),
        });
    }

    dropComponent(dragIndex, hoverIndex) {
        const { onDrop } = this.props;
        if (onDrop) {
            onDrop(dragIndex, hoverIndex);
        }
    }

    dragComponent(dragIndex) {
        const { onDrag } = this.props;
        if (onDrag) {
            onDrag(dragIndex);
        }
    }

    moveComponent(dragIndex, hoverIndex) {
        const { onMove, layoutManager } = this.props;
        this.setState({
            visibleComponents: layoutManager.getVisibleComponents(
                this.monitor.scrollTop,
                this.monitor.scrollLeft,
                true,
            ),
        });
        if (onMove) {
            onMove(dragIndex, hoverIndex);
        }
    }

    handleRepaint(nearBottomOfScroll, visibleComponents) {
        const { onLoadMoreRows, hasMoreElements, loading } = this.props;

        this.setState({ visibleComponents });

        if (hasMoreElements && !loading && nearBottomOfScroll) {
            onLoadMoreRows();
        }
    }

    render() {
        const {
            loading,
            emptyResults,
            loadingSpinner,
            hasMoreElements,
            layoutManager,
            loadingHeight,
            dataTest,
            offset,
            className,
        } = this.props;
        const { style, visibleComponents } = this.state;

        let runwayLength = layoutManager.runwayLength;
        if (loading) {
            runwayLength = runwayLength + loadingHeight;
        }

        return (
            <div
                style={style}
                data-test={dataTest}
                ref={(el) => {
                    this._scrollingElement = el;
                }}
                className={className}
            >
                <div style={Object.assign({ transform: `translateY(${runwayLength + offset}px)` }, styles.sentinel)} />
                {visibleComponents}
                {loading ? (
                    <div
                        style={{
                            position: "absolute",
                            top: `${layoutManager.runwayLength}px`,
                            left: "0px",
                            right: "0px",
                        }}
                    >
                        {loadingSpinner}
                    </div>
                ) : null}
                {!loading && !hasMoreElements && layoutManager.size === 0 ? emptyResults : null}
            </div>
        );
    }
}

export { InfiniteScrollContainer };
export const DragDropInfiniteScrollContainer = DragDropContext(DragBackend)(InfiniteScrollContainer);
export default InfiniteScrollContainer;
